// SCR-PUBLIC-015 — دریافت نسخه اطلاعات پرونده با لینک پیامکی (AP-PAT-03، AC-PAT-082.1 — C-AGENT-1351، 1358): بدون ورود؛ کد تأیید به
// موبایل اصلی مراجعه‌کننده پیامک می‌شود و پس از تأیید، بسته ZIP (PDF خلاصه پرونده و فایل ساختاریافته) دانلود و تحویل در تاریخچه مرکز ثبت
// می‌شود. لینک ۷ روز معتبر است؛ لینک نامعتبر یا منقضی ← پیام عمومی بدون جزئیات (PHX-PAT-1104). هیچ چیز در Storage مرورگر ذخیره نمی‌شود.
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type PortalOtpChallenge } from '@phoenix/api-client';
import { usePublicApi } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, latinDigits, LoadingState, toJalaliDateTime } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { saveBlob } from './common';

export const SCR_PUBLIC_015 = 'SCR-PUBLIC-015';

export function DataExportPublicPage() {
  const { t } = useTranslation('pat');
  const { token = '' } = useParams();
  const api = usePublicApi();
  const [challenge, setChallenge] = useState<PortalOtpChallenge | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => {
    document.title = `${t('pat:data_export.title')} · ${t('shell:app.staff_name')}`;
  }, [t]);
  const info = useQuery({
    queryKey: ['public', 'patient-data-export', token],
    queryFn: () => unwrap(api.GET('/api/v1/patients/data-exports/{token}', { params: { path: { token } } })),
    retry: false,
  });
  const send = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/patients/data-exports/{token}:send-otp', { params: { path: { token } } })),
    onSuccess: setChallenge,
  });
  const download = useMutation({
    mutationFn: async () => {
      const response = await api.POST('/api/v1/patients/data-exports/{token}:download', {
        params: { path: { token } },
        body: { otpId: challenge?.otpId ?? null, code: latinDigits(code).trim() },
        parseAs: 'blob',
      });
      if (!response.response.ok || !response.data) {
        await unwrap(Promise.resolve(response));
        return;
      }
      saveBlob(response.data as unknown as Blob, 'patient-data.zip');
      setDone(true);
    },
  });
  const expired = isApiError(info.error) && (info.error.status === 410 || info.error.status === 404);

  return (
    <Box data-screen-id={SCR_PUBLIC_015} sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1.5, sm: 3 } }}>
      <Paper variant="outlined" component="main" sx={{ p: { xs: 2, sm: 4 }, maxWidth: 640, mx: 'auto' }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
          <LocalHospitalOutlinedIcon color="primary" />
          <Typography sx={{ fontWeight: 600 }}>{info.data?.tenantName ?? t('shell:app.staff_name')}</Typography>
        </Stack>
        <Typography variant="h1" sx={{ mb: 2 }}>
          {t('pat:data_export.title')}
        </Typography>
        {info.isPending && <LoadingState />}
        {expired && (
          <Alert severity="warning" data-testid="data-export-expired">
            {t('pat:data_export.expired')}
          </Alert>
        )}
        {info.error && !expired ? <ErrorState error={info.error} onRetry={() => void info.refetch()} /> : null}
        {info.data && (
          <Stack spacing={2} data-testid="data-export-otp">
            <Typography>{t('pat:data_export.intro', { mobile: faDigits(info.data.mobileMasked ?? '') })}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pat:data_export.expires', { date: toJalaliDateTime(info.data.expiresAt) })}
            </Typography>
            {send.error ? <ErrorState error={send.error} compact /> : null}
            {!challenge ? (
              <Button
                variant="contained"
                onClick={() => send.mutate()}
                disabled={send.isPending}
                sx={{ alignSelf: 'flex-start' }}
                data-testid="data-export-send-otp"
              >
                {t('pat:data_export.send_code')}
              </Button>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                <TextField
                  label={t('pat:data_export.code')}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  autoComplete="one-time-code"
                  slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6, dir: 'ltr' } }}
                  data-testid="data-export-code"
                />
                <Button
                  variant="contained"
                  startIcon={<CloudDownloadOutlinedIcon />}
                  onClick={() => download.mutate()}
                  disabled={download.isPending || latinDigits(code).trim().length < 5}
                  data-testid="data-export-download"
                >
                  {t('pat:data_export.download')}
                </Button>
              </Stack>
            )}
            {download.error ? <ErrorState error={download.error} compact /> : null}
            {done && (
              <Alert severity="success" data-testid="data-export-done">
                {t('pat:data_export.downloaded')}
              </Alert>
            )}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
